import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { matchFilterNumber } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

const watchEntityKeys = ['orderLeadTimes']

const getServiceId = (leadTime, serviceProducts) => {
  const { orderable_id, orderable_type } = leadTime

  if (orderable_type !== 'ServiceProduct') return null
  return serviceProducts[orderable_id]?.service_id
}

const groupOrderLeadTimes = (filteredOrderLeadTimes, serviceProducts) => (
  filteredOrderLeadTimes.reduce((acc, leadTime) => {
    const { orderable_id, orderable_type } = leadTime
    const serviceId = orderable_type === 'Service' ? orderable_id : getServiceId(leadTime, serviceProducts)

    if (!serviceId) return acc

    if (!acc[serviceId]){
      acc[serviceId] = { productOrderLeadTimes: [] }
    }

    const serviceObject = acc[serviceId]

    // Group Service Product lead times by service. Add Service lead time if present.
    if (orderable_type === 'Service') serviceObject.serviceOrderLeadTime = leadTime
    if (orderable_type === 'ServiceProduct') serviceObject.productOrderLeadTimes.push(leadTime)

    return acc
  }, {})
)

function useOrderLeadTimes(options = {}){
  const { entityKey, filters } = options
  const {
    provider_id: filterProviderId,
  } = filters || {}

  const {
    updatedEntities: { orderLeadTimes: orderLeadTimesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { orderLeadTimes, serviceProducts } = useSelector(reduxState => reduxState.entities)

  const filteredOrderLeadTimes = useMemo(() => {
    const filtered = Object.values(orderLeadTimes).filter((orderLeadTime) => {
      const { provider_id } = orderLeadTime

      const matchProviderId = matchFilterNumber(provider_id, filterProviderId)

      return matchProviderId
    })

    return filtered
  }, [orderLeadTimesUpdatedAt, JSON.stringify(filters)])

  const hasFilteredOrderLeadTimes = !!filteredOrderLeadTimes.length
  const filteredOrderLeadTimesCount = filteredOrderLeadTimes.length

  const groupedOrderLeadTimes = groupOrderLeadTimes(filteredOrderLeadTimes, serviceProducts)

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredOrderLeadTimesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingOrderLeadTimes } = useReduxAction(
    'orderLeadTimes',
    'loadOrderLeadTimes',
    {
      entityKey,
      limit,
      ...defaultRequestOptions.orderLeadTime,
      ...filtersWithOffset,
    },
    [performHttpRequests, filtersWithOffset],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrderLeadTimesCount,
    groupedOrderLeadTimes,
    hasFilteredOrderLeadTimes,
    loading: loadingOrderLeadTimes,
  }
}

export default useOrderLeadTimes
